import { useCallback, useState } from 'react'
import { Link } from 'react-router-dom'
import { Trans, useTranslation } from 'react-i18next'
import { Alert } from '@/common'
import SignUpForm from '../components/SignUpForm'
import AuthLayout from '../components/AuthLayout'
import { useSignUp, SignUpPayload } from '../hooks/useAuth'
import { SignUpFormData } from '../components/SignUpForm.schema'

export default function Signup({
  onSuccess
}: {
  onSuccess: (payload: { email: string; password: string }) => void
}) {
  const { t } = useTranslation()
  const [serverError, setServerError] = useState<string | null>(null)
  const { mutateAsync: signup, isPending: isSigningUp } = useSignUp()

  const doSignup = useCallback(
    async (formData: SignUpFormData) => {
      setServerError(null)
      try {
        // Transform form data to API payload (excluding confirmPassword)
        const payload: SignUpPayload = {
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          password: formData.password
        }

        const signUpResult = await signup(payload)

        if (signUpResult.success === false) {
          if (signUpResult.error === 'USER_ALREADY_EXISTS') {
            setServerError('signup.errors.conflict')
          } else {
            setServerError('signup.errors.general')
          }
        } else {
          await onSuccess({
            email: payload.email,
            password: payload.password
          })
        }
        return
      } catch (error) {
        setServerError('signup.errors.general')
        console.error(error)
      }
    },
    [onSuccess, signup]
  )

  return (
    <AuthLayout>
      <>
        <div className="flex flex-col items-center space-y-4  w-full max-w-lg">
          <div className="w-full space-y-4">
            <p className="text-center">{t('signup.description')}</p>
            {serverError && <Alert type="error">{t(serverError)}</Alert>}
            <SignUpForm submit={doSignup} isSigningUp={isSigningUp} />
          </div>
          <div className="text-sm px-10 text-center">
            <Trans
              i18nKey="signup.alreadyHaveAnAccount"
              components={{
                Link: <Link className="text-green-600 underline" to="/login" />
              }}
            />
          </div>
        </div>
      </>
    </AuthLayout>
  )
}
